import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Image as ImageIcon,
  Monitor,
  MousePointer2,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Type,
  UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { api, getApiErrorMessage } from "@/lib/api";

const SCREEN_OPTIONS = [
  ["/", "Portada"],
  ["/inicio", "Inicio"],
  ["/login", "Ingreso"],
  ["/register", "Registro"],
  ["/quienes-somos", "Quiénes somos"],
  ["/mapa", "Mapa"],
  ["/calendario", "Agenda"],
  ["/directorio", "Directorio"],
  ["/conocimiento?tab=foro", "Saberes - Foro"],
  ["/conocimiento?tab=biblioteca", "Saberes - Biblioteca"],
  ["/conocimiento?tab=semillia", "Saberes - SemillIA"],
  ["/registrar-iniciativa", "Registrar iniciativa"],
  ["/crear-publicacion", "Crear publicación"],
  ["/perfil", "Perfil"],
];

function canonicalPath(value) {
  return value.split("?", 1)[0] || "/";
}

export default function ContentEditorPage() {
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [screen, setScreen] = useState("/");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [replacement, setReplacement] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [frameVersion, setFrameVersion] = useState(0);

  const frameUrl = useMemo(() => {
    const separator = screen.includes("?") ? "&" : "?";
    return `${screen}${separator}contentEditor=1&editorVersion=${frameVersion}`;
  }, [frameVersion, screen]);

  const chooseItem = useCallback((item) => {
    setSelected(item);
    setReplacement(item.currentValue || "");
  }, []);

  useEffect(() => {
    const receiveMessage = (event) => {
      if (event.origin !== window.location.origin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      if (event.data?.type === "polenica:content-inventory") {
        setItems(event.data.items || []);
      }
      if (event.data?.type === "polenica:content-selected") {
        chooseItem(event.data.item);
      }
    };
    window.addEventListener("message", receiveMessage);
    return () => window.removeEventListener("message", receiveMessage);
  }, [chooseItem]);

  useEffect(() => {
    setItems([]);
    setSelected(null);
    setReplacement("");
  }, [screen]);

  const filteredItems = items.filter((item) =>
    `${item.sourceValue} ${item.currentValue}`.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const saveOverride = async (nextValue) => {
    if (!selected || !nextValue?.trim()) {
      toast.error("Ingrese el contenido que desea publicar.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/content-editor/overrides", {
        page_path: canonicalPath(screen),
        content_kind: selected.kind,
        source_value: selected.sourceValue,
        replacement_value: nextValue.trim(),
        locator: selected.locator,
        enabled: true,
      });
      toast.success("Cambio publicado en la pantalla.");
      setFrameVersion((current) => current + 1);
      setSelected(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No fue posible publicar el cambio."));
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selected) {
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await api.post("/admin/content-editor/assets", body, { timeout: 120000 });
      await saveOverride(response.data.url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No fue posible cargar la imagen."));
      setBusy(false);
    }
  };

  const restoreOriginal = async () => {
    if (!selected?.overrideId) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/admin/content-editor/overrides/${selected.overrideId}`);
      toast.success("Contenido original restaurado.");
      setFrameVersion((current) => current + 1);
      setSelected(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No fue posible restaurar el contenido."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-content-editor-ignore="true" className="min-h-screen bg-[#E8F0E1] p-4 lg:p-6">
      <header className="mx-auto flex max-w-[1800px] flex-col gap-4 rounded-[28px] border border-primary/15 bg-[#173D29] px-5 py-5 text-white shadow-xl lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate("/admin")} className="rounded-2xl border border-white/20 p-3 hover:bg-white/10" aria-label="Volver al panel">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C9DDBA]">Editor visual protegido</p>
            <h1 className="mt-1 font-heading text-3xl font-semibold">Textos e imágenes de la plataforma</h1>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 text-sm text-[#E7F2DE]">
          <MousePointer2 className="h-4 w-4" />
          Seleccione un elemento en la vista o en el listado
        </div>
      </header>

      <div className="mx-auto mt-5 grid max-w-[1800px] gap-5 xl:grid-cols-[390px_minmax(0,1fr)]">
        <aside className="flex max-h-[calc(100vh-145px)] flex-col overflow-hidden rounded-[28px] border border-primary/15 bg-[#F6FAF2] shadow-lg">
          <div className="border-b border-primary/10 p-5">
            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/70">Pantalla</label>
            <select value={screen} onChange={(event) => setScreen(event.target.value)} className="mt-2 h-12 w-full rounded-2xl border border-primary/20 bg-white px-4 font-semibold text-primary outline-none">
              {SCREEN_OPTIONS.map(([path, label]) => <option key={path} value={path}>{label}</option>)}
            </select>
            <div className="relative mt-3">
              <Search className="absolute left-4 top-3.5 h-4 w-4 text-primary/50" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar texto visible" className="h-11 w-full rounded-2xl border border-primary/15 bg-white pl-11 pr-4 text-sm outline-none" />
            </div>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto p-3">
            {filteredItems.map((item) => (
              <button key={`${item.kind}-${item.locator}`} type="button" onClick={() => chooseItem(item)} className={`w-full rounded-2xl border p-3 text-left transition ${selected?.locator === item.locator ? "border-primary bg-[#DDEBD3]" : "border-transparent bg-white hover:border-primary/20"}`}>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 rounded-xl bg-primary/10 p-2 text-primary">{item.kind === "text" ? <Type className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}</span>
                  <div className="min-w-0">
                    <p className="line-clamp-3 text-sm font-semibold text-textMain">{item.currentValue}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-primary/55">{item.overrideId ? "Modificado" : item.kind === "text" ? "Texto original" : "Imagen original"}</p>
                  </div>
                </div>
              </button>
            ))}
            {!filteredItems.length ? <p className="px-4 py-10 text-center text-sm text-mutedForeground">Espere a que cargue la pantalla o cambie el filtro.</p> : null}
          </div>
        </aside>

        <main className="min-w-0">
          {selected ? (
            <section className="mb-4 rounded-[26px] border border-primary/15 bg-white p-5 shadow-md">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/60">{selected.kind === "text" ? "Editar texto visible" : "Reemplazar imagen"}</p>
                  {selected.kind === "text" ? (
                    <textarea value={replacement} onChange={(event) => setReplacement(event.target.value)} rows={3} className="mt-2 w-full rounded-2xl border border-primary/20 bg-[#F8FBF5] px-4 py-3 text-sm outline-none focus:border-primary" />
                  ) : (
                    <div className="mt-2 flex items-center gap-3 rounded-2xl bg-[#F1F6EC] px-4 py-3 text-sm text-textMain"><ImageIcon className="h-5 w-5 text-primary" /><span className="truncate">{selected.currentValue}</span></div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {selected.kind === "text" ? (
                    <button type="button" disabled={busy} onClick={() => saveOverride(replacement)} className="inline-flex items-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" />Publicar texto</button>
                  ) : (
                    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-semibold text-white ${busy ? "pointer-events-none opacity-50" : ""}`}><UploadCloud className="h-4 w-4" />Cargar imagen<input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadImage} className="hidden" /></label>
                  )}
                  {selected.overrideId ? <button type="button" disabled={busy} onClick={restoreOriginal} className="inline-flex items-center gap-2 rounded-2xl border border-[#9B563A]/25 bg-[#FFF3ED] px-4 py-3 text-sm font-semibold text-[#8A3D22]"><RotateCcw className="h-4 w-4" />Restaurar</button> : null}
                </div>
              </div>
            </section>
          ) : null}

          <section className="overflow-hidden rounded-[28px] border border-primary/15 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-primary/10 bg-[#F2F7ED] px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-primary"><Monitor className="h-4 w-4" />Vista real: {screen}</div>
              <button type="button" onClick={() => setFrameVersion((current) => current + 1)} className="rounded-xl p-2 text-primary hover:bg-primary/10" aria-label="Recargar vista"><RefreshCw className="h-4 w-4" /></button>
            </div>
            <iframe ref={iframeRef} key={frameUrl} src={frameUrl} title="Vista previa editable" className="h-[calc(100vh-220px)] min-h-[650px] w-full bg-white" />
          </section>
        </main>
      </div>
    </div>
  );
}
